import { BoloAssociation, BoloResource, EntityTypeEnumType } from '@mark43/cad-api';
import { HStack, VStack, IconButton, DataList, DataItem } from '@cad-arc';
import { TabIndented } from '../tabs/tab-indented';
import { useOpenEventPanel } from '../../../event-panel/use-open-event-panel';
import { useOpenBoloPanel } from '../../../bolos/bolo-panel/use-open-bolo-panel';
import { LinkText } from '../../../common/components/link-text';
import { TabNoData } from '../tabs/tab-no-data';
import React from 'react';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { useNavigateToBoloOrEvent } from '../../../bolos/bolo-panel/use-navigate-to-bolo-or-event';

export const EntityPanelBoloAssociations = observer(
    ({
        associations,
        boloIdForCurrentPanel,
        panelType,
    }: {
        associations: BoloAssociation[];
        boloIdForCurrentPanel?: number;
        panelType: 'BOLO' | 'EVENT';
    }) => {
        const apiClient = useApiClient();
        const openEventPanel = useOpenEventPanel();
        const openBoloPanel = useOpenBoloPanel();

        const unassociateBolo = useMutation((params: { boloId: number; boloLinkId: number }) => {
            return apiClient.request<BoloResource.RemoveBoloAssociation>({
                method: 'POST',
                url: `bolo/associate/remove`,
                data: params,
            });
        });

        const getTitle = (entityType: EntityTypeEnumType) => {
            if (panelType === 'EVENT') {
                return entityType === 'BOLO' ? 'Event' : 'BOLO';
            }
            return entityType === 'BOLO' ? 'BOLO' : 'Event';
        };

        const { onClickLink } = useNavigateToBoloOrEvent({
            navigateToBolo: (id) =>
                openBoloPanel({
                    boloId: id,
                    shouldFocus: true,
                }),
            navigateToEvent: (id) =>
                openEventPanel({
                    eventId: id,
                    shouldFocusPanel: true,
                }),
            currentEntityType: panelType,
            currentEntityId: boloIdForCurrentPanel,
        });

        if (!associations.length) {
            return <TabNoData />;
        }

        return (
            <VStack alignItems="flex-start">
                {associations.map(({ id, entityId, boloId, entityNumber, reason, entityType }) => {
                    return (
                        <HStack key={id} w="100%" justifyContent="space-between">
                            <DataList>
                                <DataItem dataKey={`${getTitle(entityType)} #`}>
                                    <LinkText
                                        onClick={() =>
                                            onClickLink({ entityType, boloId, entityId })
                                        }
                                    >
                                        {entityNumber}
                                    </LinkText>
                                </DataItem>
                                {reason && (
                                    <TabIndented>
                                        <DataItem dataKey="Comments">{reason}</DataItem>
                                    </TabIndented>
                                )}
                            </DataList>
                            <IconButton
                                onClick={() => unassociateBolo.mutate({ boloId, boloLinkId: id })}
                                variant="ghost"
                                icon="TrashCan"
                            />
                        </HStack>
                    );
                })}
            </VStack>
        );
    }
);
